import {
  IonButton,
  IonInput,
  IonLabel,
  IonTextarea,
  IonSelectOption,
  IonSelect,
} from "@ionic/react";
import { useEffect, useRef, useState } from "react";
import { getRTMEpisodes, SaveSetUpEpisode } from "../../services/AuthServices";
import { SetUpEpisodeErrors } from "../../models/SetUpEpisodeForm";
import { PatientVM } from "../../models/PatientVM";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";

interface Episode {
  id: number;
  name: string;
}

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

interface Props {
  setNewEpisode: Function;
  onClose?: () => void;
  rtmStartDate?: string;
}

const NewEpisodeModal: React.FC<Props> = ({
  setNewEpisode,
  onClose,
  rtmStartDate,
}) => {
  const [episodeName, setEpisodeName] = useState("");
  // const [episodeDescription, setEpisodeDescription] = useState("");
  const [errors, setErrors] = useState<SetUpEpisodeErrors>({});
  const [displayValue, setDisplayValue] = useState(""); // For displaying in the field
  const [setUpEpisodeDetail, setsetUpEpisodeDetail] =
    useState<SetUpEpisodeErrors>({
      episodeName: "",
      episodeDescription: "",
    });
  const [patientDetails, setPatientDetails] = useState<PatientVM>(
    new PatientVM(),
  );
  const [episodeOptions, setEpisodeOptions] = useState<Episode[]>([]);

  const selectRef = useRef<HTMLIonSelectElement>(null);
  const [popoverWidth, setPopoverWidth] = useState<number>(250);

  const [selectedEpisodeId, setSelectedEpisodeId] = useState<number | null>(
    null,
  );
  const [isDropDown, setIsDropDown] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarContainerRef = useRef<HTMLDivElement>(null);
  const [calendarHeight, setCalendarHeight] = useState<number>(0);
  const [value, onChange] = useState<Value>(null);
  const [normalizedDateString, setNormalizedDateString] = useState<
    string | null
  >(null);
  const [activeStartDate, setActiveStartDate] = useState(new Date());
  const isCurrentMonth =
    activeStartDate.getFullYear() === new Date().getFullYear() &&
    activeStartDate.getMonth() === new Date().getMonth();

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const calendar = document.getElementById("calendar-container");
      if (calendar && !calendar.contains(event.target as Node)) {
        setShowCalendar(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Measure calendar height and apply to spacer so the scroll container expands
  useEffect(() => {
    if (!showCalendar) {
      setCalendarHeight(0);
      return;
    }

    let observer: ResizeObserver | null = null;

    const frame = requestAnimationFrame(() => {
      const calendarEl = calendarContainerRef.current?.querySelector(
        ".react-calendar",
      ) as HTMLElement;
      if (calendarEl) {
        setCalendarHeight(calendarEl.offsetHeight);
        observer = new ResizeObserver(() =>
          setCalendarHeight(calendarEl.offsetHeight),
        );
        observer.observe(calendarEl);
      }
    });

    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [showCalendar]);

  const saveEpisode = async (episodeId: number | null, name: string | null) => {
    try {
      const EncryptedServerId = localStorage.getItem("EncryptedServerId");

      // Compute both local and UTC from the stored local ISO string
      let rtmStartDateUtc: string | null = null;
      if (normalizedDateString) {
        const dateObj = new Date(normalizedDateString); // parsed as local time
        const utcYear = dateObj.getUTCFullYear();
        const utcMonth = (dateObj.getUTCMonth() + 1)
          .toString()
          .padStart(2, "0");
        const utcDay = dateObj.getUTCDate().toString().padStart(2, "0");
        const utcHour = dateObj.getUTCHours().toString().padStart(2, "0");
        const utcMinute = dateObj.getUTCMinutes().toString().padStart(2, "0");
        const utcSecond = dateObj.getUTCSeconds().toString().padStart(2, "0");
        rtmStartDateUtc = `${utcYear}-${utcMonth}-${utcDay}T${utcHour}:${utcMinute}:${utcSecond}`;
      }

      const response = await SaveSetUpEpisode({
        episodeName: name,
        episodeId: episodeId,
        encryptedServerId: EncryptedServerId,
        patientId: localStorage.getItem("patientServerID") || "",
        masterUserId: localStorage.getItem("patientMasterID") || "",
        rtmId: 0,
        rtmStartDate: rtmStartDateUtc,
        LocalDateTime: normalizedDateString || null, // RTM start date in local time
        UTCDateTime: rtmStartDateUtc, // RTM start date in UTC
      });

      console.log("SaveSetUpEpisode response:", response);

      if (response?.data?.isResponseSuccessfull && response?.data?.id > 0) {
        setNewEpisode(false);
        onClose?.(); // safe optional call
      }

      return response;
    } catch (error) {
      console.error("Error saving episode:", error);
      throw error; // optional: rethrow if you want to handle it higher up
    }
  };

  const handleConfirm = async () => {
    let localErrors: SetUpEpisodeErrors = {};
    let isValid = true;

    if (!value) {
      localErrors.rtmStartDate = "RTM Start Date is required";
      isValid = false;
    }

    if (!selectedEpisodeId) {
      localErrors.episodeName = "Episode Name is required";
      isValid = false;
    }

    setErrors(localErrors);

    if (!isValid) return;

    console.log("Selected Episode ID:", selectedEpisodeId);
    await saveEpisode(selectedEpisodeId, null);
    return;
  };

  // Fetch episodes when component mounts
  useEffect(() => {
    debugger;
    const fetchEpisodes = async () => {
      try {
        const response = await getRTMEpisodes();
        // API returns an array of episode names
        if (response?.data) {
          setEpisodeOptions(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch episodes:", error);
      }
    };

    fetchEpisodes();
  }, []);

  useEffect(() => {
    if (selectRef.current) {
      const width = selectRef.current.getBoundingClientRect().width;
      document.documentElement.style.setProperty(
        "--select-width",
        `${width}px`,
      );
    }
  }, [episodeOptions]);

  // Also set width on mount and when component becomes visible
  useEffect(() => {
    const updateWidth = () => {
      if (selectRef.current) {
        const width = selectRef.current.getBoundingClientRect().width;
        if (width > 0) {
          // Only set if width is valid
          document.documentElement.style.setProperty(
            "--select-width",
            `${width}px`,
          );
        }
      }
    };

    // Initial width set
    updateWidth();

    // Optional: Update on resize
    window.addEventListener("resize", updateWidth);

    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        elementRef.current &&
        !elementRef.current.contains(event.target as Node)
      ) {
        setIsDropDown(false);
      }
    };

    if (isDropDown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropDown]);

  // Handle episode selection
  const handleEpisodeSelection = (option: { id: number; name: string }) => {
    debugger;
    console.log("Selected Episode ID:", option.id);

    setSelectedEpisodeId(option.id);
    setEpisodeName(option.name);
    setDisplayValue(option.name);
    setIsDropDown(false); // Close dropdown after selection
  };

  return (
    <div className="setup-episode-modal-inner-wrap-sec">
      <div className="d-flex justify-content-end">
        <svg
          onClick={() => {
            setNewEpisode(false);
          }}
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M20.9999 20.909L2.90845 2.90918M21 2.90918L2.90845 20.9091"
            stroke="#D1D1D1"
            strokeWidth="5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <h1 className="fz-32 fw-700 align-center text-charcoal-blue mb-16">
        Set up New Episode
      </h1>
      <h1 className="fz-20 fw-400 align-center text-charcoal-blue mb-24">
        Add an episode to set the timeframe for the exercise care plan.
      </h1>
      <div className="new-episode-fields-wrap ">
        <div
          className={`new-episode-date-col ${errors?.rtmStartDate ? "error-wrap" : ""}`}
        >
          <IonLabel className="label" placeholder={undefined}>
            RTM Start Date *
          </IonLabel>
          <IonInput
            className="input-field"
            type="text"
            placeholder="RTM Start Date"
            value={value instanceof Date ? value.toLocaleDateString() : ""}
            readonly
            onClick={() => setShowCalendar(!showCalendar)}
          />

          {showCalendar && (
            <div
              id="calendar-container"
              className="datepicker-calendar"
              ref={calendarContainerRef}
            >
              <Calendar
                formatShortWeekday={(locale, date) =>
                  date
                    .toLocaleDateString(locale, { weekday: "short" })
                    .slice(0, 2)
                }
                prevLabel={
                  isCurrentMonth ? null : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="13"
                      height="22"
                      viewBox="0 0 13 22"
                      fill="none"
                    >
                      <path
                        d="M10.5664 2.02707L1.51226 11.0812L10.5664 20.1354"
                        stroke="#707070"
                        stroke-width="3.01805"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  )
                }
                nextLabel={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="29"
                    height="28"
                    viewBox="0 0 29 28"
                    fill="none"
                  >
                    <path
                      d="M9.92578 5.02719L18.9799 14.0813L9.92578 23.1355"
                      stroke="#707070"
                      stroke-width="3.01805"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                }
                prev2Label={null} // optional: hide double arrows
                next2Label={null}
                minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                tileDisabled={({ date }) =>
                  date < new Date(new Date().setHours(0, 0, 0, 0))
                }
                onActiveStartDateChange={({ activeStartDate }) =>
                  setActiveStartDate(activeStartDate || new Date())
                }
                onChange={(date) => {
                  onChange(date);
                  setShowCalendar(false);
                  // Capture selected date with current local time
                  if (date instanceof Date) {
                    const now = new Date();
                    const year = date.getFullYear();
                    const month = (date.getMonth() + 1)
                      .toString()
                      .padStart(2, "0");
                    const day = date.getDate().toString().padStart(2, "0");
                    const hour = now.getHours().toString().padStart(2, "0");
                    const minute = now.getMinutes().toString().padStart(2, "0");
                    const second = now.getSeconds().toString().padStart(2, "0");
                    setNormalizedDateString(
                      `${year}-${month}-${day}T${hour}:${minute}:${second}`,
                    );
                  }
                }}
                value={value}
              />
            </div>
          )}
          <div style={{ height: calendarHeight }} />
          {errors?.rtmStartDate && (
            <p className="error-msg">{errors.rtmStartDate}</p>
          )}
        </div>
        <div
          className={`field-wrap new-episode-name-col  ${errors?.episodeName && "error-wrap"}`}
        >
          {/* <div className="field-wrap"> */}
          <IonLabel className="label" placeholder={undefined}>
            Episode Name*
          </IonLabel>

          <div>
            <div ref={elementRef} className="select-wrap-sec">
              <div
                className="input-field"
                onClick={() => {
                  setIsDropDown(!isDropDown);
                }}
              >
                {displayValue || "Select Episode"}
              </div>

              {isDropDown && (
                <div className="select-option-wrap selected">
                  <ul>
                    {episodeOptions && episodeOptions.length > 0 ? (
                      episodeOptions.map((option) => {
                        return (
                          <li className="option-wrapper" key={option.id}>
                            <div
                              className="option-name"
                              onClick={() => {
                                handleEpisodeSelection(option);
                              }}
                            >
                              <span>{option.name}</span>
                            </div>
                          </li>
                        );
                      })
                    ) : (
                      <li>No Episodes Found</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
            {errors?.episodeName && (
              <p className="error-msg">{errors.episodeName}</p>
            )}
          </div>
        </div>
      </div>

      <div className="two-col-btn">
        <IonButton
          placeholder={undefined}
          className="blue-outline-btn h-64 fz-26"
          onClick={() => setNewEpisode(false)}
        >
          Back
        </IonButton>
        <IonButton
          placeholder={undefined}
          className="blue-btn h-64 fz-26"
          onClick={handleConfirm}
        >
          Confirm
        </IonButton>
      </div>
    </div>
  );
};

export default NewEpisodeModal;
